/**
 * quick-messages-popover.tsx — Chọn câu trả lời nhanh trong ô soạn tin.
 *
 * Nguồn chính: Thư viện trả lời nhanh (GET /quick-replies), nhóm theo danh mục. Biến {{contact.firstName}}… được thay bằng
 * dữ liệu hội thoại đang mở trước khi chèn (quick-reply-vars.ts). "Quản lý thư viện" mở popup quản lý NGAY TẠI khung chat
 * (quick-replies-dialog.tsx) — không rời hội thoại. Vai trò thiếu quyền `quick_replies.view` → không hiện nút ⚡.
 *
 * Nguồn phụ (chỉ với kênh Zalo cá nhân): "tin nhắn nhanh" đồng bộ từ app Zalo
 * của nhân viên — GET /zalo-accounts/:accountId/quick-messages. Chỉ để chọn;
 * thêm/sửa làm ở thư viện. Nội dung nằm ở `message.title`, từ khoá ở `keyword`.
 *
 * Dự án KHÔNG có component Popover → dùng DropdownMenu làm popup.
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import { Zap, Search, Settings2 } from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu'
import { Loading, EmptyState, ErrorState } from '@/components/shared/feedback'
import { api, apiError } from '@/lib/api-client'
import { useAuthStore } from '@/stores/auth-store'
import { QUICK_REPLIES_PERM, useQuickReplies } from '@/hooks/use-quick-replies'
import { useMyPermissions } from '@/hooks/use-settings'
import { QuickRepliesDialog } from '@/pages/quick-replies/quick-replies-dialog'
import { ComposerToolTrigger, type ToolTriggerMode } from '@/components/shared/composer-tool-trigger'
import { renderQuickReply, type QuickReplyContext } from '@/pages/quick-replies/quick-reply-vars'

/** Tin nhắn nhanh Zalo là object passthrough từ zca-js — mọi field đều có thể vắng mặt. */
type RawQuickMessage = Record<string, unknown>

interface PickItem {
  id: string
  /** Dòng đậm phía trên: tiêu đề mẫu, hoặc /từ-khoá với tin Zalo. */
  heading: string
  content: string
  /** Có biến {{…}} cần thay — tin Zalo thì không. */
  templated: boolean
}

/** Lấy chuỗi không rỗng từ nhiều tên field khả dĩ (number cũng chấp nhận). */
function pickString(obj: RawQuickMessage, keys: string[]): string | null {
  for (const k of keys) {
    const v = obj[k]
    if (typeof v === 'string' && v.trim() !== '') return v.trim()
    if (typeof v === 'number' && Number.isFinite(v)) return String(v)
  }
  return null
}

/** Chuẩn hoá 1 tin nhanh Zalo; trả null nếu thiếu id hoặc thiếu nội dung (bỏ qua). */
function normalizeZalo(raw: unknown): PickItem | null {
  if (!raw || typeof raw !== 'object') return null
  const obj = raw as RawQuickMessage
  const id = pickString(obj, ['id', 'itemId', 'item_id'])
  if (!id) return null
  const message = (obj.message && typeof obj.message === 'object'
    ? (obj.message as RawQuickMessage)
    : {}) as RawQuickMessage
  const title = pickString(message, ['title']) ?? pickString(obj, ['title', 'content'])
  if (!title) return null
  const keyword = pickString(obj, ['keyword'])
  return { id: `zalo:${id}`, heading: keyword ? `/${keyword}` : '', content: title, templated: false }
}

interface Props {
  /** Tài khoản Zalo cá nhân của hội thoại (nếu có) — thêm nhóm tin nhanh từ app Zalo. */
  accountId?: string | null
  /** Khách của hội thoại — để thay biến {{contact.*}}. */
  contact?: QuickReplyContext['contact']
  onPick: (text: string) => void
  /** Điện thoại: 'icon' (nút tròn trên thanh công cụ) / 'tile' (ô trong hộp Tiện ích). Bỏ trống = nút của máy tính. */
  mode?: ToolTriggerMode
}

export function QuickMessagesPopover({ accountId, contact, onPick, mode }: Props) {
  const { t } = useTranslation('convChat')
  const [open, setOpen] = useState(false)
  const [manageOpen, setManageOpen] = useState(false)
  const [search, setSearch] = useState('')
  const searchRef = useRef<HTMLInputElement>(null)
  const me = useAuthStore((s) => s.user)
  const { data: perms } = useMyPermissions()
  // Chưa tải xong quyền thì vẫn hiện (máy chủ chặn lại nếu thiếu); tải xong mà thiếu quyền → ẩn hẳn.
  const allowed = !perms || perms.has(QUICK_REPLIES_PERM)

  const library = useQuickReplies(open)

  // Tin nhanh trên app Zalo: lỗi (chưa kết nối…) thì chỉ ẩn nhóm, không chặn thư viện.
  const zalo = useQuery({
    queryKey: ['zalo-quick-messages', accountId],
    enabled: open && !!accountId,
    retry: false,
    staleTime: 60 * 1000,
    queryFn: async () => {
      const { data } = await api.get<{ items?: unknown }>(`/zalo-accounts/${accountId}/quick-messages`)
      return Array.isArray(data?.items) ? (data.items as unknown[]) : []
    },
  })

  // Đưa con trỏ vào ô tìm kiếm khi mở popup; đóng thì xoá từ khoá cũ
  useEffect(() => {
    if (!open) {
      setSearch('')
      return
    }
    const timer = setTimeout(() => searchRef.current?.focus(), 0)
    return () => clearTimeout(timer)
  }, [open])

  /** Nhóm theo danh mục (theo thứ tự danh mục backend trả), "Chưa phân loại" cuối, rồi nhóm Zalo. */
  const groups = useMemo(() => {
    const kw = search.trim().toLowerCase()
    const hit = (...parts: (string | null | undefined)[]) =>
      !kw || parts.some((p) => p?.toLowerCase().includes(kw))

    const byCat = new Map<string, PickItem[]>()
    for (const q of library.data?.items ?? []) {
      if (!hit(q.title, q.content, q.category)) continue
      const key = q.category || ''
      const list = byCat.get(key) ?? []
      list.push({ id: q.id, heading: q.title, content: q.content, templated: true })
      byCat.set(key, list)
    }
    const out: { label: string; items: PickItem[] }[] = []
    for (const c of library.data?.categories ?? []) {
      const items = byCat.get(c)
      if (items?.length) out.push({ label: c, items })
    }
    const none = byCat.get('')
    if (none?.length) out.push({ label: out.length ? t('quickMessages.uncategorized') : '', items: none })

    const zaloItems = (zalo.data ?? [])
      .map(normalizeZalo)
      .filter((x): x is PickItem => !!x && hit(x.heading, x.content))
    if (zaloItems.length) out.push({ label: t('quickMessages.zaloApp'), items: zaloItems })
    return out
  }, [library.data, zalo.data, search, t])

  const total = (library.data?.items.length ?? 0) + (zalo.data?.length ?? 0)

  function openManager() {
    setOpen(false)
    // Đợi menu đóng hẳn (trả lại focus / pointer-events) rồi mới mở popup quản lý.
    setTimeout(() => setManageOpen(true), 0)
  }

  function pick(item: PickItem) {
    const text = item.templated
      ? renderQuickReply(item.content, { contact, user: me, org: me?.org })
      : item.content
    onPick(text)
    setOpen(false)
  }

  if (!allowed) return null

  return (
    <>
      <DropdownMenu open={open} onOpenChange={setOpen}>
        <DropdownMenuTrigger asChild>
          {mode ? (
            <ComposerToolTrigger mode={mode} icon={Zap} label={t('quickMessages.title')} />
          ) : (
            <Button variant="ghost" size="icon" aria-label={t('quickMessages.title')} title={t('quickMessages.title')}>
              <Zap className="h-4 w-4" />
            </Button>
          )}
        </DropdownMenuTrigger>

        <DropdownMenuContent
          align="start"
          side="top"
          sideOffset={8}
          className="w-96 p-2"
          // Không để menu cướp phím gõ (typeahead) khi đang nhập từ khoá
          onKeyDown={(e) => e.stopPropagation()}
        >
          <div className="relative mb-2">
            <Search className="pointer-events-none absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              ref={searchRef}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('quickMessages.searchPlaceholder')}
              aria-label={t('quickMessages.search')}
              className="h-9 pl-8"
            />
          </div>

          {library.isError ? (
            <ErrorState message={apiError(library.error)} />
          ) : library.isPending ? (
            <Loading className="py-10" label={t('common:status.loading')} />
          ) : total === 0 ? (
            <EmptyState icon={Zap} title={t('quickMessages.empty')} />
          ) : groups.length === 0 ? (
            <div className="px-2 py-8 text-center text-sm text-muted-foreground">
              {t('quickMessages.notFound')}
            </div>
          ) : (
            <div className="max-h-80 space-y-2 overflow-y-auto">
              {groups.map((g) => (
                <div key={g.label || '_'}>
                  {g.label && (
                    <p className="sticky top-0 z-[1] bg-popover px-2 py-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      {g.label}
                    </p>
                  )}
                  <ul className="space-y-0.5">
                    {g.items.map((item) => (
                      <li key={item.id}>
                        <button
                          type="button"
                          onClick={() => pick(item)}
                          className="w-full rounded-md px-2 py-1.5 text-left transition-colors hover:bg-accent"
                        >
                          {item.heading && (
                            <span className="block truncate text-xs font-semibold text-primary">{item.heading}</span>
                          )}
                          <span className="line-clamp-2 text-sm text-foreground">{item.content}</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          )}

          <DropdownMenuSeparator />
          <button
            type="button"
            onClick={openManager}
            className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-sm font-medium text-primary transition-colors hover:bg-accent"
          >
            <Settings2 className="h-4 w-4" />
            {t('quickMessages.manage')}
          </button>
        </DropdownMenuContent>
      </DropdownMenu>
      <QuickRepliesDialog open={manageOpen} onOpenChange={setManageOpen} />
    </>
  )
}
